import React, { useCallback, useEffect, useRef } from "react"
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api"
import { Box, Typography } from "@material-ui/core"
import RoomIcon from "@material-ui/icons/Room"
import type { MapaRoteirizacaoBaseProps, MarcadorPedido } from "./tipos"
import { CENTRO_PADRAO, rotuloMarcador } from "./tipos"
import { CASA_SVG_DATA_URL, CASA_TAMANHO } from "./icones"

const containerStyle = { width: "100%", height: "100%" }

const opcoesMapa = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    clickableIcons: false,
}

const rotuloStyle: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 700,
    lineHeight: 1,
    color: "#1a1a1a",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(0,0,0,0.2)",
    borderRadius: 8,
    padding: "2px 5px",
    marginBottom: 3,
    whiteSpace: "nowrap",
    boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
}

function bolaStyle(m: MarcadorPedido): React.CSSProperties {
    const tamanho = m.selecionado ? 20 : 15
    return {
        width: tamanho,
        height: tamanho,
        borderRadius: "50%",
        background: m.cor,
        border: m.selecionado ? "3px solid #2e7d32" : "2px solid #ffffff",
        boxShadow: "0 0 4px rgba(0,0,0,0.5)",
    }
}

export function GoogleMapaRoteirizacao({
    origem,
    marcadores,
    onSelecionarPedido,
}: MapaRoteirizacaoBaseProps) {
    const mapRef = useRef<any>(null)
    const idsKey = marcadores.map(m => m.id).join(",")

    const ajustarLimites = useCallback(
        (map: any) => {
            const google = (window as any).google
            if (!google || !google.maps) return
            const pontos = marcadores.map(m => ({ lat: m.latitude, lng: m.longitude }))
            if (origem) pontos.push(origem)
            if (pontos.length === 0) return
            if (pontos.length === 1) {
                map.setCenter(pontos[0])
                map.setZoom(15)
                return
            }
            const bounds = new google.maps.LatLngBounds()
            pontos.forEach(p => bounds.extend(p))
            map.fitBounds(bounds)
        },
        [marcadores, origem]
    )

    const onLoad = useCallback(
        (map: any) => {
            mapRef.current = map
            ajustarLimites(map)
        },
        [ajustarLimites]
    )

    // Reenquadra quando o conjunto de pedidos muda (não a cada seleção).
    useEffect(() => {
        if (mapRef.current) ajustarLimites(mapRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey])

    const google = (window as any).google
    if (!google || !google.maps) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                p={3}
                style={{ textAlign: "center", color: "#7f8c8d" }}
            >
                <RoomIcon style={{ fontSize: 48, marginBottom: 8 }} />
                <Typography variant="body1">
                    Mapa indisponível: o token do Google Maps não está configurado para este
                    estabelecimento.
                </Typography>
            </Box>
        )
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={origem ?? CENTRO_PADRAO}
            zoom={origem ? 13 : 4}
            onLoad={onLoad}
            options={opcoesMapa}
        >
            {origem && (
                <Marker
                    position={origem}
                    title="Restaurante"
                    icon={{
                        url: CASA_SVG_DATA_URL,
                        scaledSize: new google.maps.Size(CASA_TAMANHO, CASA_TAMANHO),
                        anchor: new google.maps.Point(CASA_TAMANHO / 2, CASA_TAMANHO / 2),
                    }}
                    zIndex={999}
                />
            )}

            {marcadores.map(m => (
                <OverlayView
                    key={m.id}
                    position={{ lat: m.latitude, lng: m.longitude }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                        x: -(width / 2),
                        y: -(height - 8),
                    })}
                >
                    <div
                        onClick={e => {
                            e.stopPropagation()
                            onSelecionarPedido(m.id)
                        }}
                        title={`#${m.shortReference} - ${m.cliente}${
                            m.nomeEntregador ? ` · ${m.nomeEntregador}` : ""
                        }`}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            cursor: "pointer",
                        }}
                    >
                        <div style={rotuloStyle}>{rotuloMarcador(m)}</div>
                        <div style={bolaStyle(m)} />
                    </div>
                </OverlayView>
            ))}
        </GoogleMap>
    )
}
